/* @jsx h */
import { defineComponent, ref, computed, h, onMounted, watch } from "vue";
import * as Sentry from "@sentry/vue";
import { api } from "@app/data/index";
import { formatPrice } from "@lemmonhq/orders";
import {
  useCheckout,
  useToast,
  useNetworkStatus,
  useTrpc,
} from "@app/vue/providers/index";
import {
  Order,
  TableStatus,
  SuccesfullPartialPayment,
  RawPayment,
  CheckoutData,
  PaymentDetails,
  TableCredit,
} from "@lemmonhq/types";
import { Loading, LemmonModal } from "@app/vue/components/index";
import {
  createPartialPaymentPayload,
  calculateRemainingTotal,
  createSplitPaymentPayload,
  calculatePaidTotal,
  getRemainingUnpaidItemQuantities,
} from "./paymentUtils";
import { calculateOrderTotal, round } from "@lemmonhq/orders";

import { PartialPaymentContent } from "./PartialPaymentContent";
import { SplitPaymentContent } from "./SplitPaymentContent";
import { ConfirmReduceQuantitiesModal } from "./ConfirmReduceQuantitiesModal";
import { calculatePartialTotal } from "@app/utils/payments";
import { ErrorRenderer, useLocalization } from "@lemmonhq/uikit";
import { useAuth } from "@app/vue/providers/Auth";
import {
  TableCreditCloseDialog,
  TableCreditTopUp,
  parseTableCreditError,
  tableCreditOf,
} from "@app/vue/components/TableCredit";
//
// Five states, not three (`ready | loading | failed`), because two splits
// each carry real behavioral weight:
//
// - `initial_loading` vs `ready`: only `initial_loading` triggers the
//   full-screen spinner (see the render gate below). Once we've loaded once,
//   a later fetch stays in `ready`-adjacent territory instead of blanking
//   out valid, already-visible payment content.
// - `load_failed` vs `post_payment_verification_failed`: this is the actual
//   point of this state machine. A single `failed` state can't safely cover
//   both "table failed to load, nothing risky happened" and "a payment may
//   have just gone through and we can't confirm it" — collapsing them either
//   shows the "do not charge again" warning on routine load hiccups (alarm
//   fatigue) or shows the routine message after a real payment (the original
//   bug this exists to fix: a refresh failure looking identical to "nothing
//   happened" invites a double charge). They also drive different retry
//   actions — `retryLoad` just re-fetches; `retryVerification` looks up the
//   specific pending payment by id/guid and never resubmits it.
//
// `refreshing` covers the post-checkout window in `refreshAfterCheckout`,
// which runs while `modalVisible` is false. It has to be part of the
// spinner gate below: that gate sits above the `!modalVisible` early
// return, so it's the only thing keeping this component from rendering
// nothing at all while the confirmation fetch is in flight. (Before the
// split into `loadSnapshot`/`fetchData`, that refresh went through
// `fetchData` and got the spinner via `loading` for free.)
// When live refresh with the modal visible lands
// (table_payment_live_refresh.md), this is where a subtler in-place
// indicator should replace the full spinner swap — but it must keep
// rendering *something*.
type TablePaymentLoadState =
  | "initial_loading"
  | "ready"
  | "refreshing"
  | "load_failed"
  | "post_payment_verification_failed";

type PendingVerification = {
  paymentId?: number;
  paymentGuid?: string;
};

const extractPaymentIdentifiers = (
  data: CheckoutData | null,
): PendingVerification => {
  const txSuccess = data?.txSuccess;
  const payment =
    txSuccess && txSuccess.type !== "staff_checkout_payment_collected"
      ? txSuccess.payment
      : undefined;
  if (payment) return { paymentId: payment.id, paymentGuid: payment.guid };
  if (typeof data?.paymentId === "number") return { paymentId: data.paymentId };
  return {};
};

export type TablePaymentProps = {
  tableStatus: TableStatus;
  onClose: Function;
  onSuccess: (closed?: boolean) => void;
};

export type PaymentModes = "partial" | "split";

export const calculatePaymentMode = (
  payments: RawPayment[],
): PaymentModes | undefined => {
  if (payments.length == 0) return;
  const hasSplitPayment = payments.find((el) => el.split_payment_id);
  if (hasSplitPayment) return "split";
  return "partial";
};
//
export const TablePayment = defineComponent<TablePaymentProps>({
  name: "TablePayment",
  props: ["tableStatus", "onClose", "onSuccess"],
  setup(props) {
    const auth = useAuth();
    const orders = ref<Order[]>([]);
    const expandedGroups = ref<Set<string>>(new Set());
    const showPaidPayments = ref(false);
    const partialPayments = ref<SuccesfullPartialPayment[]>([]);
    const donePayments = ref<PaymentDetails[]>([]);
    const integrityWarnings = ref<Record<string, any>[]>([]);
    const partialSelectedItems = ref<Record<number, number>>({});
    const loading = ref(true);
    const modalVisible = ref(true);
    const paymentMode = ref<PaymentModes>("partial");
    const splitAmount = ref(0);
    const splitCustomAmount = ref(0);
    const splitCount = ref<number>(2);
    const lastErrorMessage = ref<string | null>(null);
    const loadState = ref<TablePaymentLoadState>("initial_loading");
    const pendingVerification = ref<PendingVerification | null>(null);
    const retrying = ref(false);
    const retryCount = ref(0);
    const { t } = useLocalization();
    const trpc = useTrpc();
    // Open table credit, read from the same fresh snapshot as the orders. Its
    // orders are on the bill; Checkout spends the credit first. Closing it
    // settles the leftover, so it never auto-closes
    // (docs/plans/table_credit/pos_checkout_credit.md).
    const tableCredit = ref<TableCredit | null>(null);
    const creditCloseBalance = ref<number | null>(null);
    const showCreditTopUp = ref(false);
    // Once an amount-level split is paid (a guest split, or table credit that
    // covered part of an order), items can no longer be paid or removed one by
    // one; "Show items" lists them read-only.
    const showItemsReadOnly = ref(false);

    const checkout = useCheckout();
    const toast = useToast();
    const network = useNetworkStatus();
    const activeSplitTab = ref("equal"); // 'equal' or 'custom'

    // Pure fetch — assigns the refs but decides nothing about closing the
    // table. Callers that want the "balance nets to zero" auto-close behavior
    // call `maybeAutoClose()` themselves. Kept separate so a post-checkout
    // verification refresh can never trigger it as a side effect.
    const loadSnapshot = async () => {
      const tableId = props.tableStatus.id;
      const tableStatus = (await api.getTablesMeta()).find(
        (el) => el.id === tableId,
      );
      if (!tableStatus) throw new Error("table not found");
      tableCredit.value = tableCreditOf(tableStatus);
      const orderIds = tableStatus.orders.map((order) => order.id);
      if (orderIds.length === 0) {
        orders.value = [];
        partialPayments.value = [];
        donePayments.value = [];
        integrityWarnings.value = [];
        partialSelectedItems.value = {};
        return { orders: [], partialPayments: [], payments: [] };
      }
      const [ordersData, paymentsData] = await Promise.all([
        api.getOrders(orderIds),
        api.getTablePayments(orderIds),
      ]);
      orders.value = ordersData;
      partialPayments.value = paymentsData.partial_payments || [];
      integrityWarnings.value = paymentsData.integrity_warnings || [];
      donePayments.value = (paymentsData.payments || []).filter(
        (el) => el.status === "success" || el.status === "authorized",
      );
      partialSelectedItems.value = {};
      if (donePayments.value.length > 0) {
        paymentMode.value = calculatePaymentMode(
          donePayments.value,
        ) as PaymentModes;
      }
      return {
        orders: ordersData,
        payments: paymentsData.payments || [],
        partialPayments: paymentsData.partial_payments || [],
      };
    };

    // Reducing every item, or a partial payment, can bring the table to a
    // zero balance without an explicit "close" action — this is what used to
    // live inside `fetchData` unconditionally.
    const maybeAutoClose = async () => {
      // Closing a table with credit settles its leftover, which the cashier
      // must choose (revenue or tip); it only happens on an explicit close.
      if (tableCredit.value) return;
      const hasSomeCustomerOrders = orders.value.some((el) => !el.user_id);
      if (Math.abs(remainingTotal.value) <= 0.01 && !hasSomeCustomerOrders) {
        await handleCloseOrder();
      }
    };

    const fetchData = async () => {
      try {
        loading.value = true;
        const snapshot = await loadSnapshot();
        loadState.value = "ready";
        lastErrorMessage.value = null;
        await maybeAutoClose();
        return snapshot;
      } catch (error: any) {
        console.error("Failed to fetch data:", error);
        loadState.value = "load_failed";
        lastErrorMessage.value =
          error?.message || t("tables.checkout.load_failed");
        throw error;
      } finally {
        loading.value = false;
      }
    };

    const captureRefreshFailure = (
      context: { outcome: "success" | "cancel" } & PendingVerification,
      error: unknown,
    ) => {
      Sentry.captureException(
        new Error("Table payment post-checkout verification failed"),
        {
          level: "error",
          tags: { invariant: "table_payment_verification_failed" },
          extra: {
            outcome: context.outcome,
            paymentId: context.paymentId,
            paymentGuid: context.paymentGuid,
            restaurantId: auth.user.value?.restaurant.id,
            tableId: props.tableStatus.id,
            orderIds: orders.value.map((o) => o.id),
            retryCount: retryCount.value,
            networkOffline: network.isBrowserOffline.value,
            networkDegraded: network.isFetchFailing.value,
            cause: error instanceof Error ? error.message : String(error),
          },
        },
      );
    };

    const reportedNegativeBalance = ref(false);

    // Shared by both checkout callbacks so a failed refresh carries enough
    // context to recover safely. Whether the blocking state means "we don't
    // know if this succeeded" (post_payment_verification_failed) rather than
    // an ordinary load error is decided by whether we captured a payment
    // identifier at all — not by which callback fired. A cancel racing a
    // just-completed payment is exactly as uncertain as a success whose
    // confirmation fetch failed.
    const refreshAfterCheckout = async (
      context: { outcome: "success" | "cancel" } & PendingVerification,
    ) => {
      try {
        loadState.value = "refreshing";
        await loadSnapshot();
        lastErrorMessage.value = null;
        pendingVerification.value = null;
        if (context.outcome === "success") {
          await maybeAutoClose();
        }
        // Stays `refreshing` until every await above has settled, then flips
        // in the same synchronous block as the `modalVisible` restore below.
        // Going `ready` earlier would leave a window where the spinner gate
        // is off but `modalVisible` is still false — one render of `null`,
        // which on mobile unmounts and re-animates the whole BottomSheet.
        loadState.value = "ready";
      } catch (error) {
        const hasPaymentContext = Boolean(
          context.paymentId || context.paymentGuid,
        );
        loadState.value = hasPaymentContext
          ? "post_payment_verification_failed"
          : "load_failed";
        if (hasPaymentContext) {
          pendingVerification.value = {
            paymentId: context.paymentId,
            paymentGuid: context.paymentGuid,
          };
        }
        lastErrorMessage.value =
          error instanceof Error ? error.message : String(error);
        captureRefreshFailure(context, error);
      } finally {
        modalVisible.value = true;
      }
    };

    // Never resubmits the original payment — only looks up whether it has
    // since appeared in a fresh fetch. If it hasn't, we genuinely cannot
    // distinguish "still pending" from "conclusively failed" without a
    // provider/workflow-status lookup this plan doesn't add, so it stays
    // blocked rather than guessing either way.
    const retryVerification = async () => {
      if (retrying.value) return;
      const pending = pendingVerification.value;
      if (!pending) return;
      retrying.value = true;
      retryCount.value += 1;
      try {
        const snapshot = await loadSnapshot();
        const matched = snapshot.payments.some(
          (payment) =>
            (payment.status === "success" || payment.status === "authorized") &&
            ((pending.paymentId !== undefined &&
              payment.id === pending.paymentId) ||
              (pending.paymentGuid !== undefined &&
                payment.guid === pending.paymentGuid)),
        );
        if (matched) {
          loadState.value = "ready";
          lastErrorMessage.value = null;
          pendingVerification.value = null;
          await maybeAutoClose();
        } else {
          loadState.value = "post_payment_verification_failed";
        }
      } catch (error) {
        loadState.value = "post_payment_verification_failed";
        lastErrorMessage.value =
          error instanceof Error ? error.message : String(error);
        captureRefreshFailure({ outcome: "success", ...pending }, error);
      } finally {
        retrying.value = false;
      }
    };

    const retryLoad = async () => {
      if (retrying.value) return;
      retrying.value = true;
      try {
        await loadSnapshot();
        loadState.value = "ready";
        lastErrorMessage.value = null;
        await maybeAutoClose();
      } catch (error) {
        loadState.value = "load_failed";
        lastErrorMessage.value =
          error instanceof Error ? error.message : String(error);
      } finally {
        retrying.value = false;
      }
    };

    onMounted(async () => {
      try {
        await fetchData();
      } catch {
        // fetchData() already recorded load_failed + lastErrorMessage.
      }
    });

    const subTotal = computed(() =>
      orders.value.reduce((sum, order) => sum + calculateOrderTotal(order), 0),
    );
    const paidTotal = computed(() => calculatePaidTotal(donePayments.value));
    const remainingTotal = computed(() =>
      calculateRemainingTotal(orders.value, donePayments.value),
    );
    const isNegativeBalance = computed(() => remainingTotal.value < -0.01);

    // Reports once per negative streak, not on every re-render: flips back
    // to false as soon as the balance is no longer negative, so a later
    // recurrence reports again.
    watch(remainingTotal, (value) => {
      if (value >= -0.01) {
        reportedNegativeBalance.value = false;
        return;
      }
      if (reportedNegativeBalance.value) return;
      reportedNegativeBalance.value = true;
      Sentry.captureException(
        new Error("Negative table payment balance detected"),
        {
          level: "error",
          tags: { invariant: "table_payment_negative_remaining" },
          extra: {
            remainingTotal: value,
            tableId: props.tableStatus.id,
            orderIds: orders.value.map((o) => o.id),
          },
          fingerprint: ["table-payment-negative-remaining"],
        },
      );
    });

    const allPaymentItems = computed(() =>
      orders.value.map((order) => order.items || []).flat(),
    );
    const remainingUnpaidItems = computed(() =>
      getRemainingUnpaidItemQuantities(orders.value, partialPayments.value),
    );
    const hasSelectedPartialItems = computed(
      () => Object.keys(partialSelectedItems.value).length > 0,
    );
    const selectedItemsForNextPayment = computed(() =>
      hasSelectedPartialItems.value
        ? partialSelectedItems.value
        : remainingUnpaidItems.value,
    );

    const areAllUnpaidItemsSelected = computed(() => {
      const remainingEntries = Object.entries(remainingUnpaidItems.value);

      return (
        remainingEntries.length > 0 &&
        remainingEntries.every(
          ([itemId, remainingQty]) =>
            partialSelectedItems.value[Number(itemId)] === remainingQty,
        ) &&
        Object.keys(partialSelectedItems.value).length ===
          remainingEntries.length
      );
    });

    const handleSelectAllUnpaid = () => {
      if (areAllUnpaidItemsSelected.value) {
        // Deselect all
        partialSelectedItems.value = {};
      } else {
        // Select all unpaid
        partialSelectedItems.value = { ...remainingUnpaidItems.value };
      }
    };

    const handlePartialPayment = async () => {
      const selectedItems = selectedItemsForNextPayment.value;
      if (Object.keys(selectedItems).length === 0) {
        toast.showToast("No unpaid items are available to pay", {
          type: "warning",
          icon: "receipt",
        });
        return;
      }
      const payload = createPartialPaymentPayload(
        orders.value,
        selectedItems,
        props.tableStatus.table.id,
        partialPayments.value,
      );
      showCheckout({
        payload: { type: "table", object: payload },
        serviceMethod: "dine-in",
      });
    };

    const handleSplitPayment = async () => {
      if (splitAmount.value > 0) {
        const payload = createSplitPaymentPayload(
          orders.value,
          partialPayments.value,
          props.tableStatus.table.id,
          splitAmount.value,
          donePayments.value.filter((el) => el.split_payment_id).length,
          activeSplitTab.value,
        );
        showCheckout({
          payload: { type: "table", object: payload },
          serviceMethod: "dine-in",
        });
      }
    };

    const showCheckout = (checkoutData: CheckoutData) => {
      const credit = tableCredit.value;
      checkout.setCheckout({
        ...checkoutData,
        tableCredit:
          credit?.credited && credit.balance > 0
            ? { tableId: props.tableStatus.table.id, available: credit.balance }
            : null,
      });
      modalVisible.value = false;
      checkout.show({
        onCancel: async () => {
          // Read before Checkout.tsx clears its state once this callback's
          // promise settles — it now awaits the callback instead of clearing
          // synchronously, so this is still populated here.
          const ids = extractPaymentIdentifiers(checkout.checkout.value);
          await refreshAfterCheckout({ outcome: "cancel", ...ids });
        },
        onSuccess: async () => {
          const ids = extractPaymentIdentifiers(checkout.checkout.value);
          await refreshAfterCheckout({ outcome: "success", ...ids });
        },
      });
    };

    const handleCloseOrder = async () => {
      console.log("remainingTotal.value", remainingTotal.value);
      if (tableCredit.value) {
        await handleCloseCreditedTable();
        return;
      }
      try {
        loading.value = true;
        await api.closeTable(props.tableStatus.id);
        props.onSuccess(true);
        props.onClose();
      } catch (e) {
        toast.showToast("Failed to close table", {
          type: "error",
          icon: "table_restaurant",
        });
      } finally {
        loading.value = false;
      }
    };
    // Without a settlement choice the server refuses a table with leftover
    // credit and reports the balance; then the cashier picks revenue or tip.
    const handleCloseCreditedTable = async () => {
      try {
        loading.value = true;
        await trpc.tableCredit.closeTable.mutate({
          tableId: props.tableStatus.id,
        });
        props.onSuccess(true);
        props.onClose();
      } catch (e: any) {
        const parsed = parseTableCreditError(e?.message);
        if (parsed?.code === "settlement_required") {
          creditCloseBalance.value = parsed.balance;
        } else if (parsed?.code === "payment_pending") {
          toast.showToast(t("pos.table_credit.payment_pending"), {
            type: "warning",
            icon: "table_restaurant",
          });
        } else {
          toast.showToast(e?.message || "Failed to close table", {
            type: "error",
            icon: "table_restaurant",
          });
        }
      } finally {
        loading.value = false;
      }
    };

    const showRemoveConfirmModal = ref(false);

    const handleRemoveItems = () => {
      showRemoveConfirmModal.value = true;
    };

    const handleItemsReduced = async () => {
      showRemoveConfirmModal.value = false;
      await fetchData();
    };

    return () => {
      if (
        loading.value ||
        loadState.value === "initial_loading" ||
        loadState.value === "refreshing"
      ) {
        return (
          <LemmonModal
            title={`${props.tableStatus.table.name} ${t(
              "tables.checkout.modal_header",
            )}`}
            onClose={props.onClose}
            renderButtons={() => null}
          >
            <div class="flex justify-center py-32">
              <Loading />
            </div>
          </LemmonModal>
        );
      }

      if (
        loadState.value === "load_failed" ||
        loadState.value === "post_payment_verification_failed"
      ) {
        const isVerification =
          loadState.value === "post_payment_verification_failed";
        const offlineState: "offline" | "degraded" | false = network
          .isBrowserOffline.value
          ? "offline"
          : network.isFetchFailing.value
            ? "degraded"
            : false;
        return (
          <LemmonModal
            title={`${props.tableStatus.table.name} ${t(
              "tables.checkout.modal_header",
            )}`}
            onClose={props.onClose}
            renderButtons={() => (
              <div class="p-3 lg:p-4 border-t flex justify-end gap-2">
                <button class="btn btn-neutral" onClick={() => props.onClose()}>
                  {t("general.words.close")}
                </button>
              </div>
            )}
          >
            <div class="p-2 lg:p-4">
              {isVerification && (
                <p class="text-center text-sm font-semibold text-warning mb-2">
                  {t("tables.checkout.verification_failed_title")}
                </p>
              )}
              <ErrorRenderer
                message={
                  isVerification
                    ? t("tables.checkout.verification_failed_body")
                    : lastErrorMessage.value || t("tables.checkout.load_failed")
                }
                severity={isVerification ? "warning" : "error"}
                retrying={retrying.value}
                offline={offlineState}
                retryLabel={
                  isVerification
                    ? t("tables.checkout.retry_verification")
                    : undefined
                }
                onRetry={isVerification ? retryVerification : retryLoad}
              />
            </div>
          </LemmonModal>
        );
      }

      if (showRemoveConfirmModal.value) {
        const allItems = orders.value.flatMap((order) => order.items || []);
        const confirmItems = Object.keys(partialSelectedItems.value).map(
          (key) => {
            const id = parseInt(key);
            const item = allItems.find((el) => el.id === id);
            return {
              id,
              quantity: partialSelectedItems.value[id],
              name: item?.product?.name ?? String(id),
            };
          },
        );
        return (
          <ConfirmReduceQuantitiesModal
            items={confirmItems}
            tableId={props.tableStatus.table.id}
            onSuccess={handleItemsReduced}
            onClose={() => {
              showRemoveConfirmModal.value = false;
            }}
          />
        );
      }

      if (creditCloseBalance.value !== null) {
        return (
          <TableCreditCloseDialog
            tableId={props.tableStatus.id}
            tableName={props.tableStatus.table.name}
            balance={creditCloseBalance.value}
            currency={auth.user.value?.restaurant.currency || "CHF"}
            onCancel={() => (creditCloseBalance.value = null)}
            onClosed={() => {
              creditCloseBalance.value = null;
              props.onSuccess(true);
              props.onClose();
            }}
          />
        );
      }

      if (showCreditTopUp.value) {
        return (
          <TableCreditTopUp
            tableId={props.tableStatus.id}
            tableName={props.tableStatus.table.name}
            suggestedAmount={Math.max(
              remainingTotal.value - (tableCredit.value?.balance || 0),
              0,
            )}
            onClose={() => (showCreditTopUp.value = false)}
            onDone={async () => {
              await fetchData();
            }}
          />
        );
      }

      if (!modalVisible.value) return null;
      const currency = auth.user.value?.restaurant.currency || "CHF";
      // Credit Checkout takes first on a payment (docs/plans/table_credit).
      // The credit bar shows what was loaded on the table and what is left
      // right now. With credit left, Pay shows no amount (Checkout works out
      // what the credit covers); otherwise it shows the amount to collect.
      const credited = !!tableCredit.value?.credited;
      const creditLoaded = credited ? tableCredit.value!.loaded : 0;
      const creditBalance =
        credited && tableCredit.value!.balance > 0.005
          ? tableCredit.value!.balance
          : 0;
      const payLabel = (amount: number) =>
        creditBalance > 0 ? (
          <span>{t("general.words.pay")}</span>
        ) : (
          <>
            <span class="hidden xl:inline">{t("general.words.pay")} </span>
            {formatPrice(amount, true, 2, currency)}
          </>
        );
      const hasSplitPayment = donePayments.value.some(
        (el) => el.split_payment_id,
      );
      const viewingItems =
        showItemsReadOnly.value &&
        paymentMode.value == "split" &&
        hasSplitPayment;
      const nextPaymentTotal =
        paymentMode.value == "partial"
          ? Object.keys(selectedItemsForNextPayment.value).length === 0
            ? remainingTotal.value
            : calculatePartialTotal(
                allPaymentItems.value,
                selectedItemsForNextPayment.value,
              )
          : splitAmount.value;
      //df
      return (
        <LemmonModal
          title={`${props.tableStatus.table.name} ${t(
            "tables.checkout.modal_header",
          )}`}
          onClose={props.onClose}
          renderButtons={() => {
            return (
              <div class="p-3 lg:p-4 border-t ">
                <div class="flex justify-between">
                  <div class="flex gap-2 flex-1">
                    <>
                      {paymentMode.value == "split" && hasSplitPayment && (
                        <button
                          class={
                            viewingItems ? "btn btn-accent" : "btn btn-neutral"
                          }
                          onClick={() => {
                            showItemsReadOnly.value = !viewingItems;
                          }}
                        >
                          <span class="material-symbols-outlined  text-2xl">
                            {viewingItems ? "arrow_split" : "receipt_long"}
                          </span>{" "}
                          <span class="hidden xl:inline">
                            {viewingItems
                              ? t("tables.checkout.back_to_payment")
                              : t("tables.checkout.show_items")}
                          </span>
                        </button>
                      )}
                      {paymentMode.value == "split" && !hasSplitPayment && (
                        <button
                          class="btn btn-neutral"
                          onClick={() => {
                            paymentMode.value = "partial";
                          }}
                        >
                          <span class="material-symbols-outlined  text-2xl">
                            receipt
                          </span>{" "}
                          <span class="hidden xl:inline">
                            {t("general.words.back")}
                          </span>
                        </button>
                      )}
                      {paymentMode.value == "partial" && (
                        <>
                          {!donePayments.value.find(
                            (el) => el.partial_payment_id,
                          ) && (
                            <button
                              class="btn btn-accent"
                              onClick={() => {
                                paymentMode.value = "split";
                              }}
                            >
                              <span class="material-symbols-outlined  text-2xl">
                                arrow_split
                              </span>{" "}
                              <span class="hidden xl:inline">
                                {t("tables.checkout.split_button")}
                              </span>
                            </button>
                          )}
                          <button
                            class="btn btn-neutral w-[48px] h-[48px]"
                            onClick={handleSelectAllUnpaid}
                          >
                            <span class="material-symbols-outlined  text-2xl">
                              {areAllUnpaidItemsSelected.value
                                ? "playlist_remove"
                                : "done_all"}
                            </span>{" "}
                          </button>
                        </>
                      )}
                    </>
                    <>
                      {paymentMode.value == "partial" &&
                      Object.keys(partialSelectedItems.value).length > 0 ? (
                        <button
                          disabled={
                            Object.keys(partialSelectedItems.value).length === 0
                          }
                          onClick={() => handleRemoveItems()}
                          class="btn w-[48px] h-[48px] btn-error p-0"
                        >
                          <span class="material-symbols-outlined text-3xl">
                            delete
                          </span>
                        </button>
                      ) : null}
                    </>
                  </div>
                  <div class="flex gap-2">
                    {Math.abs(remainingTotal.value) <= 0.01 ? (
                      <>
                        <button
                          onClick={handleCloseOrder}
                          class="btn btn-primary ml-2"
                        >
                          <span class="inline">
                            {t("tables.checkout.close_orders")}
                          </span>
                        </button>
                      </>
                    ) : (
                      <>
                        {paymentMode.value === "partial" && (
                          <button
                            onClick={() => {
                              console.log(
                                "value ",
                                round(nextPaymentTotal),
                                round(remainingTotal.value),
                                nextPaymentTotal,
                                remainingTotal.value,
                              );
                              if (
                                round(nextPaymentTotal) >
                                round(remainingTotal.value)
                              ) {
                                alert("Payment exceeds remaining");
                                return;
                              }
                              handlePartialPayment();
                            }}
                            disabled={
                              nextPaymentTotal <= 0 || isNegativeBalance.value
                            }
                            class="btn btn-primary"
                          >
                            <span class="material-symbols-outlined text-2xl">
                              receipt
                            </span>{" "}
                            {payLabel(nextPaymentTotal)}
                          </button>
                        )}
                        {paymentMode.value === "split" && (
                          <button
                            onClick={() => {
                              console.log([
                                nextPaymentTotal,
                                remainingTotal.value,
                              ]);
                              console.log(
                                "value ",
                                round(nextPaymentTotal),
                                round(remainingTotal.value),
                                nextPaymentTotal,
                                remainingTotal.value,
                              );
                              if (
                                round(nextPaymentTotal) >
                                round(remainingTotal.value)
                              ) {
                                alert("Payment exceeds remaining");
                                return;
                              }
                              handleSplitPayment();
                            }}
                            class="btn btn-primary"
                            disabled={
                              viewingItems ||
                              !Number.isFinite(splitAmount.value) ||
                              splitAmount.value === 0 ||
                              isNegativeBalance.value
                            }
                          >
                            <span class="material-symbols-outlined  text-2xl">
                              arrow_split
                            </span>{" "}
                            {payLabel(splitAmount.value)}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          }}
        >
          <div class="p-2 lg:p-4 flex flex-col overflow-y-hidden">
            {credited ? (
              <div class="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border bg-base-200 px-3 py-2">
                <span class="flex items-center gap-4">
                  <span class="material-symbols-outlined text-base text-base-content/55">
                    account_balance_wallet
                  </span>
                  <span class="flex flex-col leading-tight">
                    <span class="text-xs text-base-content/60">
                      {t("pos.table_credit.title")}
                    </span>
                    <span class="font-semibold tabular-nums">
                      {formatPrice(creditLoaded, true, 2, currency)}
                    </span>
                  </span>
                  <span class="flex flex-col leading-tight hidden">
                    <span class="text-xs text-base-content/60">
                      {t("tables.checkout.remaining")}
                    </span>
                    <span class="font-semibold tabular-nums text-accent">
                      {formatPrice(creditBalance, true, 2, currency)}
                    </span>
                  </span>
                </span>
                <button
                  type="button"
                  class="btn btn-sm btn-ghost"
                  onClick={() => (showCreditTopUp.value = true)}
                >
                  {t("pos.table_credit.add_credit")}
                </button>
              </div>
            ) : null}
            <>
              {paymentMode.value === "partial" && (
                <PartialPaymentContent
                  orders={orders.value}
                  expandedGroups={expandedGroups}
                  partialPayments={partialPayments.value}
                  selectedItems={partialSelectedItems.value}
                  onSelectItem={(id, qty) => {
                    if (qty === 0) {
                      const { [id]: _, ...rest } = partialSelectedItems.value;
                      partialSelectedItems.value = rest;
                    } else {
                      partialSelectedItems.value = {
                        ...partialSelectedItems.value,
                        [id]: qty,
                      };
                    }
                  }}
                />
              )}
              {viewingItems && (
                <>
                  <PartialPaymentContent
                    orders={orders.value}
                    expandedGroups={expandedGroups}
                    partialPayments={partialPayments.value}
                    selectedItems={{}}
                  />
                </>
              )}
              {paymentMode.value === "split" && !viewingItems && (
                <SplitPaymentContent
                  remainingTotal={remainingTotal.value}
                  donePayments={donePayments.value}
                  splitAmount={splitAmount}
                  splitCount={splitCount}
                  subtotal={subTotal}
                  activeSplitTab={activeSplitTab}
                  splitCustomAmount={splitCustomAmount}
                />
              )}
            </>

            <div class="border-t  pt-4 text-accent text-xl mt-4 select-none">
              <div class="flex justify-between items-center text-sm">
                <div>
                  <span class="w-8 inline-block"></span>
                  {t("tables.checkout.subtotal")}
                </div>
                <div>{formatPrice(subTotal.value, true, 2, currency)}</div>
              </div>
              <div
                class="flex justify-between items-center text-sm cursor-pointer"
                onClick={() =>
                  (showPaidPayments.value = !showPaidPayments.value)
                }
              >
                <div class="flex items-center">
                  <span class="w-8 flex">
                    {donePayments.value && donePayments.value.length > 0 && (
                      <span class="material-symbols-outlined ">
                        {showPaidPayments.value
                          ? "keyboard_arrow_down"
                          : "chevron_right"}
                      </span>
                    )}
                  </span>
                  {t("tables.checkout.paid")}
                  {donePayments.value.length > 0
                    ? ` [${donePayments.value.length}]`
                    : null}
                </div>
                <div>{formatPrice(paidTotal.value, true, 2, currency)}</div>
              </div>
              {showPaidPayments.value && donePayments.value.length > 0 && (
                <div class="pl-8 py-2 space-y-2">
                  {donePayments.value.map((payment) => (
                    <div
                      key={payment.id}
                      class="flex justify-between items-center text-sm text-accent/50"
                    >
                      <div class="flex items-center gap-2">
                        <span class="capitalize">{payment.method}</span>
                        <span class="text-xs">
                          ({new Date(payment.created_at).toLocaleString()})
                        </span>
                        <span class="uppercase text-xs">{payment.status}</span>
                      </div>
                      <div>
                        {formatPrice(payment.amount, true, 2, currency)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div class="flex justify-between items-center">
                <div class="font-bold">
                  <span class="w-8 inline-block"></span>
                  {t("tables.checkout.remaining")}
                </div>
                <div>
                  {formatPrice(remainingTotal.value, true, 2, currency)}
                </div>
              </div>
              {isNegativeBalance.value && (
                <div class="text-error text-sm mt-1">
                  {t("tables.checkout.negative_balance_warning")}
                </div>
              )}
            </div>
          </div>
        </LemmonModal>
      );
    };
  },
});
